import {
  Person,
  Business,
  CorporateFare,
  Groups,
  AccountBalance,
  VolunteerActivism,
} from '@mui/icons-material'

export const businessTypes = [
  {
    title: 'Proprietorship',
    description:
      'A business owned and operated by a single individual. Simple structure with minimal compliance requirements and direct personal liability.',
    icon: Person,
    documents: [
      {
        name: 'pan_card_proprietor',
        label: 'PAN Card of Proprietor',
        description:
          "Upload a clear copy of the proprietor's PAN card issued by the Income Tax Department",
        type: 'file',
        required: true,
      },
      {
        name: 'aadhaar_proprietor',
        label: 'Aadhaar of Proprietor',
        description: "Upload a clear copy of the proprietor's Aadhaar card issued by UIDAI",
        type: 'file',
        required: true,
      },
      {
        name: 'bank_account_proof',
        label: 'Bank Account Proof (Firm name if available)',
        description:
          'Upload bank statement or passbook showing account details, preferably with firm name',
        type: 'file',
        required: true,
      },
      {
        name: 'gst_certificate',
        label: 'GST Certificate',
        description: 'Upload GST registration certificate if your business is registered under GST',
        type: 'file',
        required: false,
      },
      {
        name: 'udyam_msme_certificate',
        label: 'Udyam/MSME Certificate',
        description:
          'Upload Udyam registration certificate or MSME certificate for additional benefits',
        type: 'file',
        required: false,
      },
    ],
  },
  {
    title: 'Partnership Firm',
    description:
      'A business structure where two or more individuals share ownership, profits, and responsibilities. Registered under the Indian Partnership Act 1932.',
    icon: Groups,
    documents: [
      {
        name: 'pan_firm',
        label: 'PAN of the Firm',
        description:
          'Upload the PAN card of the partnership firm issued by the Income Tax Department',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'partnership_deed',
        label: 'Partnership Deed',
        description:
          'Upload the registered partnership deed document outlining terms and conditions of partnership',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'gst_certificate',
        label: 'GST Certificate',
        description: 'Upload GST registration certificate of the partnership firm',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'partners_aadhaar_pan',
        label: 'Aadhaar & PAN of all Partners',
        description: 'Upload Aadhaar and PAN cards of all partners involved in the partnership',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
        multiple: true,
      },
      {
        name: 'bank_account_proof',
        label: 'Bank Account Proof',
        description: "Upload bank statement or passbook of the partnership firm's account",
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'registration_certificate',
        label: 'Registration Certificate',
        description:
          'Upload partnership registration certificate if the firm is registered with Registrar of Firms',
        type: 'file',
        required: false,
      },
    ],
  },
  {
    title: 'Limited Liability Partnership (LLP)',
    description:
      'A hybrid business structure combining features of companies and partnerships. Provides limited liability protection while maintaining operational flexibility.',
    icon: Business,
    documents: [
      {
        name: 'incorporation_certificate_mca',
        label: 'Incorporation Certificate (MCA)',
        description:
          'Upload the Certificate of Incorporation issued by Ministry of Corporate Affairs (MCA)',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'llp_agreement',
        label: 'LLP Agreement',
        description: 'Upload the LLP Agreement document filed with MCA outlining partnership terms',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'pan_llp',
        label: 'PAN of LLP',
        description: 'Upload the PAN card of the Limited Liability Partnership',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'gst_certificate',
        label: 'GST Certificate',
        description: 'Upload GST registration certificate of the LLP',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'office_address_proof',
        label: 'Office Address Proof',
        description:
          'Upload utility bill, rent agreement, or property documents as office address proof',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'designated_partners_docs',
        label: 'PAN & Aadhaar of Designated Partners',
        description: 'Upload PAN and Aadhaar cards of all designated partners of the LLP',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
        multiple: true,
      },
      {
        name: 'bank_account_proof',
        label: 'Bank Account Proof',
        description: "Upload bank statement or passbook of the LLP's account",
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
    ],
  },
  {
    title: 'Private Limited Company',
    description:
      'A separate legal entity with limited liability for shareholders. Registered under the Companies Act 2013 with enhanced credibility and growth potential.',
    icon: CorporateFare,
    documents: [
      {
        name: 'certificate_incorporation_mca',
        label: 'Certificate of Incorporation (MCA)',
        description:
          'Upload the Certificate of Incorporation issued by Ministry of Corporate Affairs',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'pan_company',
        label: 'PAN of Company',
        description: 'Upload the PAN card of the Private Limited Company',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'gst_certificate',
        label: 'GST Certificate',
        description: 'Upload GST registration certificate of the company',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'moa_aoa',
        label: 'MOA & AOA',
        description: 'Upload Memorandum of Association and Articles of Association documents',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'board_resolution',
        label: 'Board Resolution for Authorized Signatory',
        description:
          'Upload board resolution authorizing specific person(s) to act on behalf of the company',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'directors_docs',
        label: 'PAN & Aadhaar of Directors',
        description: 'Upload PAN and Aadhaar cards of all directors of the company',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
        multiple: true,
      },
      {
        name: 'bank_account_proof',
        label: 'Bank Account Proof',
        description: "Upload bank statement or passbook of the company's account",
        type: 'file',
        required: true,
      },
      {
        name: 'mca_master_data',
        label: 'MCA Master Data',
        description: 'Upload MCA master data or company information sheet (optional)',
        type: 'file',
        required: false,
      },
    ],
  },
  {
    title: 'One Person Company (OPC)',
    description:
      'A corporate structure for single entrepreneurs providing limited liability benefits. Combines sole proprietorship simplicity with company advantages.',
    icon: AccountBalance,
    documents: [
      {
        name: 'same_as_pvt_ltd',
        label: 'Same as Pvt Ltd (single director)',
        description:
          'Upload all documents similar to Private Limited Company but for single director structure',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'nominee_consent_form',
        label: 'Nominee Consent Form',
        description: 'Upload signed consent form from the nominated person for the OPC',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'incorporation_certificate',
        label: 'Incorporation Certificate',
        description: 'Upload the Certificate of Incorporation for One Person Company issued by MCA',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
    ],
  },
  {
    title: 'NGO / Trust / Society',
    description:
      'Non-profit organizations focused on social causes and community welfare. Registered under respective acts with tax exemption benefits.',
    icon: VolunteerActivism,
    documents: [
      {
        name: 'registration_certificate',
        label: 'Registration Certificate',
        description:
          'Upload registration certificate of the NGO/Trust/Society from appropriate authority',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'pan_entity',
        label: 'PAN of Entity',
        description: 'Upload PAN card of the NGO/Trust/Society entity',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'trust_deed_society_rules',
        label: 'Trust Deed / Society Rules',
        description: 'Upload trust deed document or society rules and regulations',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'board_resolution_authorization',
        label: 'Board Resolution / Authorization Letter',
        description: 'Upload board resolution or authorization letter for authorized signatory',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'trustees_docs',
        label: 'PAN & Aadhaar of Trustees',
        description: 'Upload PAN and Aadhaar cards of all trustees/managing committee members',
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
        multiple: true,
      },
      {
        name: 'bank_account_proof',
        label: 'Bank Account Proof',
        description: "Upload bank statement or passbook of the organization's account",
        type: 'file',
        required: false,
        mandatoryIfPostpaid: true,
      },
      {
        name: 'gst_certificate',
        label: 'GST Certificate',
        description:
          'Upload GST registration certificate if the organization is registered under GST',
        type: 'file',
        required: false,
      },
    ],
  },
]
